import { c as _c } from "react/compiler-runtime";
import { FocusTrap, TransitionChild } from '@headlessui/react';
import { ArrowIcon } from '@tallinn-arvudes/ui/atoms/icons/arrow-icon';
import { CloseIcon } from '@tallinn-arvudes/ui/atoms/icons/close-icon';
import { SearchIcon } from '@tallinn-arvudes/ui/atoms/icons/search-icon';
import { KEY_EVENT_KEYS } from '@tallinn-arvudes/ui/constants';
import { Button } from '@tallinn-arvudes/ui/tallinn-design/Button';
import { Input } from '@tallinn-arvudes/ui/tallinn-design/Input';
import { cn } from '@tallinn-arvudes/ui/utils';
import { HOME_SLUG, METRICS_PATH, SEARCH_CONSTANTS, STATISTICS_PATH } from '@/constants';
import { type SearchPartial } from '@/features/search/api';
import { api } from '@/trpc/react';
import { Chip } from '@tallinn-arvudes/ui/tallinn-design/Chip';
import { Link } from '@tallinn-arvudes/ui/tallinn-design/Link';
import { useRouter } from 'next/navigation';
import React, { type FC, type InputHTMLAttributes, type JSX, type RefObject, useEffect, useRef } from 'react';
import { useResizeObserver } from 'usehooks-ts';
interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string;
  onClear: () => void;
  ref?: RefObject<HTMLInputElement | null>;
  onChange: (query: string) => void;
  showClearBtn: boolean;
  onClose?: () => void;
  loading?: boolean;
  triggerSearch: () => void;
  debouncedValue: string;
}
export const SearchInput: FC<SearchInputProps> = t0 => {
  const $ = _c(78);
  let autoFocus;
  let debouncedValue;
  let loading;
  let onChange;
  let onClear;
  let onClose;
  let props;
  let ref;
  let showClearBtn;
  let triggerSearch;
  if ($[0] !== t0) {
    ({
      ref,
      onClear,
      onChange,
      onClose,
      showClearBtn,
      loading,
      autoFocus,
      triggerSearch,
      debouncedValue,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = autoFocus;
    $[2] = debouncedValue;
    $[3] = loading;
    $[4] = onChange;
    $[5] = onClear;
    $[6] = onClose;
    $[7] = props;
    $[8] = ref;
    $[9] = showClearBtn;
    $[10] = triggerSearch;
  } else {
    autoFocus = $[1];
    debouncedValue = $[2];
    loading = $[3];
    onChange = $[4];
    onClear = $[5];
    onClose = $[6];
    props = $[7];
    ref = $[8];
    showClearBtn = $[9];
    triggerSearch = $[10];
  }
  const [canClose, setCanClose] = React.useState(true);
  const clearButtonRef = useRef(null);
  const searchButtonRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const contentRef = useRef(null);
  let t1;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ref: contentRef as RefObject<HTMLDivElement>
    };
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const {
    height
  } = useResizeObserver(t1);
  let t2;
  let t3;
  if ($[12] !== autoFocus) {
    t2 = () => {
      if (autoFocus && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
      }
    };
    t3 = [autoFocus];
    $[12] = autoFocus;
    $[13] = t2;
    $[14] = t3;
  } else {
    t2 = $[13];
    t3 = $[14];
  }
  useEffect(t2, t3);
  let t4;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => {
      if (e.key === "Tab" && resultsRef.current) {
        const focusableElements = resultsRef.current.querySelectorAll("a, button, [tabindex]:not([tabindex=\"-1\"])");
        const lastElement = focusableElements[focusableElements.length - 1];
        if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          inputRef.current?.focus();
        }
      }
    };
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleResultsKeyDown = t4;
  let t5;
  if ($[16] !== triggerSearch) {
    t5 = e_0 => {
      if (e_0.key === KEY_EVENT_KEYS.ENTER) {
        triggerSearch?.();
      }
      if (e_0.key === "Tab" && e_0.shiftKey && resultsRef.current) {
        const focusableElements_0 = resultsRef.current.querySelectorAll("a, button, [tabindex]:not([tabindex=\"-1\"])");
        const lastElement_0 = focusableElements_0[focusableElements_0.length - 1];
        e_0.preventDefault();
        lastElement_0?.focus();
      }
    };
    $[16] = triggerSearch;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleInputKeyDown = t5;
  let t6;
  if ($[18] !== canClose || $[19] !== onClose) {
    t6 = e_1 => {
      if (e_1.key === KEY_EVENT_KEYS.ESC && canClose) {
        onClose?.();
      }
    };
    $[18] = canClose;
    $[19] = onClose;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      inputRef.current?.focus();
    };
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center" aria-hidden={true}><SearchIcon className="h-6 w-6 text-semantic-content-brand" /></div>;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== ref) {
    t9 = node => {
      inputRef.current = node;
      if (ref) {
        ref.current = node;
      }
    };
    $[23] = ref;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("min-h-12 pl-12 pr-20 z-[55]");
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== onChange) {
    t11 = e_2 => {
      onChange(e_2.target.value);
    };
    $[26] = onChange;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== handleInputKeyDown || $[29] !== props || $[30] !== t11 || $[31] !== t9) {
    t12 = <Input autoComplete="off" id="search-bar" ref={t9} onKeyDown={handleInputKeyDown} aria-label="Otsi" className={t10} onChange={t11} {...props} />;
    $[28] = handleInputKeyDown;
    $[29] = props;
    $[30] = t11;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const t13 = !showClearBtn;
  const t14 = !props.value || loading;
  let t15;
  if ($[33] !== t13 || $[34] !== t14) {
    t15 = cn("absolute right-10 top-1/2 -translate-y-1/2 h-8 w-8", {
      "right-2": t13,
      hidden: t14
    });
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== triggerSearch) {
    t16 = () => {
      triggerSearch?.();
    };
    $[36] = triggerSearch;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ArrowIcon className="h-4 w-4" />;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t15 || $[40] !== t16) {
    t18 = <Button ref={searchButtonRef} className={t15} type="button" variant="icon" onClick={t16} aria-label="Otsi">{t17}</Button>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== loading || $[43] !== showClearBtn) {
    t19 = loading && <div className={cn("right-11 absolute top-1/2 -translate-y-1/2 text-primary", {
      "right-4": !showClearBtn
    })}><div className="h-6 w-6 animate-spin rounded-full border-3 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white" role="status"><span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Laeb...</span></div></div>;
    $[42] = loading;
    $[43] = showClearBtn;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== showClearBtn) {
    t20 = cn("hidden absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8", {
      block: showClearBtn
    });
    $[45] = showClearBtn;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <CloseIcon className="h-6 w-6" />;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== onClear || $[49] !== t20) {
    t22 = <Button ref={clearButtonRef} className={t20} type="button" variant="icon" onClick={onClear} aria-label={"T\xFChjenda otsing"}>{t21}</Button>;
    $[48] = onClear;
    $[49] = t20;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t12 || $[52] !== t18 || $[53] !== t19 || $[54] !== t22) {
    t23 = <div onClick={t7} className="relative w-full z-[55]">{t8}{t12}{t18}{t19}{t22}</div>;
    $[51] = t12;
    $[52] = t18;
    $[53] = t19;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  let t25;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      setCanClose(true);
    };
    t25 = () => {
      setCanClose(false);
    };
    $[56] = t24;
    $[57] = t25;
  } else {
    t24 = $[56];
    t25 = $[57];
  }
  const t26 = height ? `calc(${height}px + 1rem)` : 0;
  const t27 = height ? 1 : 0;
  let t28;
  if ($[58] !== t26 || $[59] !== t27) {
    t28 = {
      height: t26,
      opacity: t27
    };
    $[58] = t26;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = cn("motion-reduce:transition-none pl-10 pr-4 py-2 max-h-[50vh] md:max-h-[70vh] overflow-y-auto absolute top-[calc(100%)] data-[enter]:delay-300 data-[enter]:duration-300 data-[leave]:duration-200 transition-all w-full z-50 rounded-md bg-popover text-popover-foreground shadow-md outline-none", "data-[closed]:-top-0 data-[closed]:opacity-0");
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  const t30 = debouncedValue !== props.value;
  let t31;
  if ($[62] !== debouncedValue || $[63] !== onChange || $[64] !== onClose || $[65] !== t30 || $[66] !== triggerSearch) {
    t31 = <div ref={contentRef}><SuggestionResult handleSearch={triggerSearch} onChange={onChange} onClose={onClose} isStale={t30} query={debouncedValue} /></div>;
    $[62] = debouncedValue;
    $[63] = onChange;
    $[64] = onClose;
    $[65] = t30;
    $[66] = triggerSearch;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== t28 || $[69] !== t31) {
    t32 = <TransitionChild afterEnter={t24} beforeEnter={t25}><div ref={resultsRef} onKeyDown={handleResultsKeyDown} tabIndex={-1} style={t28} className={t29}>{t31}</div></TransitionChild>;
    $[68] = t28;
    $[69] = t31;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  const t33 = canClose ? onClose : undefined;
  let t34;
  if ($[71] !== t33) {
    t34 = <div onClick={t33} className="fixed h-full w-full top-0 left-0 bg-black/10" />;
    $[71] = t33;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== t23 || $[74] !== t32 || $[75] !== t34 || $[76] !== t6) {
    t35 = <FocusTrap className="relative w-full" onKeyDown={t6}>{t23}{t32}{t34}</FocusTrap>;
    $[73] = t23;
    $[74] = t32;
    $[75] = t34;
    $[76] = t6;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  return t35;
};
const SuggestionResult = t0 => {
  const $ = _c(50);
  const {
    query,
    isStale,
    onClose,
    onChange,
    handleSearch
  } = t0;
  const router = useRouter();
  const t1 = !query;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      staleTime: Infinity,
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: defaultData,
    isLoading: suggestionsLoading,
    isFetched,
    error: suggestionsError
  } = api.search.getDefaultSuggestions.useQuery(undefined, t2);
  let t3;
  if ($[2] !== query) {
    t3 = {
      query
    };
    $[2] = query;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = !!query;
  let t5;
  if ($[4] !== t4) {
    t5 = {
      enabled: t4,
      staleTime: Infinity,
      _optimisticResults: "optimistic"
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    data,
    isLoading,
    error
  } = api.search.getSuggestions.useQuery(t3, t5);
  const isEmpty = Object.values(data || {}).every(_temp);
  let t6;
  if ($[6] !== data) {
    t6 = Object.values(data || {}).some(_temp2);
    $[6] = data;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const hasMoreResults = t6;
  let t7;
  if ($[8] !== onClose || $[9] !== query || $[10] !== router) {
    t7 = href => {
      const queryParam = `?${SEARCH_CONSTANTS.QUERY_PARAM}=${query}`;
      window.history.replaceState(null, "", window.location.pathname + queryParam);
      router.push(href + queryParam);
      onClose?.();
    };
    $[8] = onClose;
    $[9] = query;
    $[10] = router;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const onItemClick = t7;
  if (suggestionsLoading) {
    let t8;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="flex items-center text-semantic-content-brand font-bold text-xs h-14">Laen otsingu soovitusi...</div>;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    return t8;
  }
  if (!query.length && isFetched && (defaultData && !defaultData.length || suggestionsError)) {
    let t8;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="flex items-center text-semantic-content-brand font-bold text-xs h-14">Otsingu soovitusi ei leitud.</div>;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  if (!query.length && defaultData && defaultData.length > 0) {
    let t8;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <span className="text-semantic-content-brand font-bold text-xs">Soovitame</span>;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== defaultData || $[16] !== onChange || $[17] !== query) {
      let t10;
      if ($[19] !== onChange || $[20] !== query) {
        t10 = item_1 => <Chip contentProps={{
          side: "bottom"
        }} key={item_1.id} onClick={() => {
          onChange(item_1.name);
        }}>{highlightAllMatches(item_1.name, query)}</Chip>;
        $[19] = onChange;
        $[20] = query;
        $[21] = t10;
      } else {
        t10 = $[21];
      }
      t9 = defaultData.map(t10);
      $[15] = defaultData;
      $[16] = onChange;
      $[17] = query;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[22] !== t9) {
      t10 = <div>{t8}<div className="flex gap-2 flex-wrap mt-2">{t9}</div></div>;
      $[22] = t9;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    return t10;
  }
  if (isLoading) {
    let t8;
    if ($[24] !== query) {
      t8 = <span className="flex items-center text-semantic-content-brand font-bold text-xs h-14">Laen tulemusi otsingusõnale "{query}"...</span>;
      $[24] = query;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    return t8;
  }
  if (error) {
    const t8 = error.message || "Midagi l\xE4ks valesti. Proovi hiljem uuesti.";
    let t9;
    if ($[26] !== t8) {
      t9 = <span className="flex items-center text-semantic-content-brand font-bold text-xs h-14">{t8}</span>;
      $[26] = t8;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    return t9;
  }
  if (isEmpty) {
    let t8;
    if ($[28] !== query) {
      t8 = <span className="flex items-center text-semantic-content-brand font-bold text-xs h-14">Otsingusõnale "{query}" vastavaid tulemusi ei leitud.</span>;
      $[28] = query;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    return t8;
  }
  let t8;
  if ($[30] !== data || $[31] !== isStale || $[32] !== onItemClick || $[33] !== query || $[34] !== suggestionsLoading) {
    let t9;
    if ($[36] !== data) {
      t9 = data || {};
      $[36] = data;
      $[37] = t9;
    } else {
      t9 = $[37];
    }
    let t10;
    if ($[38] !== isStale || $[39] !== onItemClick || $[40] !== query || $[41] !== suggestionsLoading) {
      t10 = t11 => {
        const [key, value] = t11;
        if (!value.docs.length) {
          return null;
        }
        const title = getSectionTitle(key);
        return <div key={key} className="flex flex-col">{title && <span className="text-semantic-content-brand font-bold text-xs">{title}</span>}<ResultList onItemClick={onItemClick} className="my-1" query={query} isStale={isStale || suggestionsLoading} docs={value.docs} /></div>;
      };
      $[38] = isStale;
      $[39] = onItemClick;
      $[40] = query;
      $[41] = suggestionsLoading;
      $[42] = t10;
    } else {
      t10 = $[42];
    }
    t8 = Object.entries(t9).map(t10);
    $[30] = data;
    $[31] = isStale;
    $[32] = onItemClick;
    $[33] = query;
    $[34] = suggestionsLoading;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[43] !== handleSearch || $[44] !== hasMoreResults || $[45] !== query) {
    t9 = hasMoreResults && <Link onClick={e => {
      e.preventDefault();
      handleSearch();
    }} href={`${SEARCH_CONSTANTS.SEARCH_BASE_URL}?${SEARCH_CONSTANTS.QUERY_PARAM}=${query}`} className="text-semantic-content-brand text-xs font-bold w-max p-1" iconPosition="right" icon={<ArrowIcon className="!h-3 !w-3" />}>Kõik tulemused</Link>;
    $[43] = handleSearch;
    $[44] = hasMoreResults;
    $[45] = query;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  let t10;
  if ($[47] !== t8 || $[48] !== t9) {
    t10 = <div className="flex flex-col">{t8}{t9}</div>;
    $[47] = t8;
    $[48] = t9;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  return t10;
};
const getSectionTitle = (type: string | 'metrics' | 'pages' | 'statistics') => {
  switch (type) {
    case 'metrics':
      return 'Mõõdikud';
    case 'pages':
      return 'Lehed';
    case 'statistics':
      return 'Statistika';
    default:
      return '';
  }
};
const ResultList = ({
  docs,
  query,
  isStale,
  className,
  onItemClick
}: {
  docs: SearchPartial[];
  query: string;
  isStale: boolean;
  className?: string;
  onItemClick: (href: string) => void;
}) => {
  return docs.map(item => {
    const href = getPath(item);
    return <Link onClick={e => {
      e.preventDefault();
      onItemClick(href);
    }} href={href} key={item.id} variant="inline-neutral" className={cn('px-2 py-1 leading-tight !no-underline hover:outline-none hover:!text-semantic-content-brand focus:!outline outline-semantic-focus-outer outline-2 -outline-offset-4 rounded-md focus:!shadow-none flex-col items-start', {
      'opacity-50 pointer-events-none': isStale
    }, className)}>
        <span className="text-sm">
          {highlightAllMatches(item.title, query)}
        </span>
      </Link>;
  });
};
function highlightAllMatches(text: string | null | undefined, query: string): React.ReactNode {
  if (!query || !text) return text;
  const regex = new RegExp(escapeRegExp(query.trim()), 'gi');
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const start = match.index;
    const end = regex.lastIndex;

    // Push text before match
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    // Push matched part wrapped in <strong>
    parts.push(<strong key={start}>{text.slice(start, end)}</strong>);
    lastIndex = end;
  }

  // Push any remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return <>{parts}</>;
}

// Helper to escape special RegExp characters in the query
function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
const getPath = ({
  doc,
  slug
}: SearchPartial) => {
  let path = '';
  if (doc.relationTo === 'metrics') {
    path = `${METRICS_PATH}/${typeof doc.value === 'number' ? doc.value : doc.value.id}`;
  } else if (doc.relationTo === 'statistics') {
    path = `${STATISTICS_PATH}/${typeof doc.value === 'number' ? doc.value : doc.value.id}`;
  } else if (doc.relationTo === 'pages') {
    path = slug === HOME_SLUG ? '/' : slug || '';
  }
  return path;
};
function _temp(item) {
  if (Array.isArray(item.docs)) {
    return item.docs.length === 0;
  }
  return false;
}
function _temp2(item_0) {
  return item_0.hasMoreResults;
}